import { Button, Popover, Table, TableProps } from 'antd';
import { FC, memo } from 'react';
import { Question } from 'react-bootstrap-icons';

import { CopyableText } from '@/common/components';

export const PagesHelpNotes: FC = memo(() => {

  return (
    <Popover
      content={
        <div style={{ maxHeight: 620, overflowY: 'auto' }}>
          <Table
            size="small"
            dataSource={ items }
            columns={ columns }
            showHeader={ false }
            pagination={ false }
            bordered
            expandable={{
              expandedRowRender: (record) => <p style={{ margin: 0 }}>{ record?.example }</p>,
              rowExpandable: (record) => record.type === 'function',
            }}
          />
        </div>
      }
      title="Змінні, доступні в сторінках, шаблонах та компонентах"
      trigger={ ['click'] }
      styles={{ body: { maxWidth: 800 } }}
      placement="bottomRight"
    >
      <Button shape="round" icon={ <Question size={ 20 }/> } style={{ paddingInline: 8 }}/>
    </Popover>
  );
});

const items = [
  {
    key: 'currentFqdn',
    name: <CopyableText
      textToCopy="currentFqdn"
      style={{ fontWeight: 'bold', color: 'black', margin: 0 }}
    >
      currentFqdn
    </CopyableText>,
    description: 'Домен в форматі FQDN в контексті запиту на цей домен з мережі Інтернет.',
    type: 'variable',
  },
  {
    key: 'currentDomain',
    name: <CopyableText
      textToCopy="currentDomain"
      style={{ fontWeight: 'bold', color: 'black', margin: 0 }}
    >
      currentDomain
    </CopyableText>,
    description: 'Назва домену в контексті запиту на цей домен з мережі Інтернет.',
    type: 'variable',
  },
  {
    key: 'mainFqdn',
    name: <CopyableText
      textToCopy="mainFqdn"
      style={{ fontWeight: 'bold', color: 'black', margin: 0 }}
    >
      mainFqdn
    </CopyableText>,
    description: <span>Головний домен сайту в форматі FQDN. <i>(В режимі попереднього перегляду відсутній)</i></span>,
    type: 'variable',
  },
  {
    key: 'mainDomain',
    name: <CopyableText
      textToCopy="mainDomain"
      style={{ fontWeight: 'bold', color: 'black', margin: 0 }}
    >
      mainDomain
    </CopyableText>,
    description: <span>
      Назва головного домену сайту в контексті запиту на цей домен з мережі Інтернет.&nbsp;
      <i>(В режимі попереднього перегляду відсутній)</i>
    </span>,
    type: 'variable',
  },
  {
    key: 'title',
    name: <CopyableText
      textToCopy="title"
      style={{ fontWeight: 'bold', color: 'black', margin: 0 }}
    >
      title
    </CopyableText>,
    description: 'Заголовок сторінки, вказавний в SEO розділі налаштувань сторінки.',
    type: 'variable',
  },
  {
    key: 'pagePath',
    name: <CopyableText
      textToCopy="pagePath"
      style={{ fontWeight: 'bold', color: 'black', margin: 0 }}
    >
      pagePath
    </CopyableText>,
    description: 'URL шлях сторінки який дорівнює слагу ноди.',
    type: 'variable',
  },
  {
    key: 'canonical',
    name: <CopyableText
      textToCopy="canonical"
      style={{ fontWeight: 'bold', color: 'black', margin: 0 }}
    >
      canonical
    </CopyableText>,
    description: <span>Повний тег { '<link rel="canonical">' } з відповідним { 'href' }.&nbsp;
      <i>(В режимі попереднього перегляду відсутній)</i>
    </span>,
    type: 'variable',
  },
  {
    key: 'critical',
    name: <CopyableText
      textToCopy="critical"
      style={{ fontWeight: 'bold', color: 'black', margin: 0 }}
    >
      critical
    </CopyableText>,
    description: <span>
      Згенерований Critical CSS сторінки (лише CSS, без тегу).
      Приклад: <code>{ '<style>{{critical}}</style>' }</code>.
    </span>,
    type: 'variable',
  },
  {
    key: 'pageCreatedAt',
    name: <CopyableText
      textToCopy="pageCreatedAt"
      style={{ fontWeight: 'bold', color: 'black', margin: 0 }}
    >
      pageCreatedAt
    </CopyableText>,
    description: 'Дата створення сторінки в форматі ISO 8601.',
    type: 'variable',
  },
  {
    key: 'pageUpdatedAt',
    name: <CopyableText
      textToCopy="pageUpdatedAt"
      style={{ fontWeight: 'bold', color: 'black', margin: 0 }}
    >
      pageUpdatedAt
    </CopyableText>,
    description: 'Дата останнього редагування сторінки в форматі ISO 8601.',
    type: 'variable',
  },
  {
    key: 'isModified',
    name: <CopyableText
      textToCopy="isModified"
      style={{ fontWeight: 'bold', color: 'black', margin: 0 }}
    >
      isModified
    </CopyableText>,
    description: 'Логічне значення (true/false) – чи редагувалась сторінка після створення.',
    type: 'variable',
  },
  {
    key: 'modifiedTime',
    name: <CopyableText
      textToCopy="modifiedTime"
      style={{ fontWeight: 'bold', color: 'black', margin: 0 }}
    >
      modifiedTime
    </CopyableText>,
    // eslint-disable-next-line max-len
    description: 'Функція для отримання дати останнього редагування сторінки в форматі ISO 8601 (якщо формат не вказаний). Більше деталей: https://day.js.org/docs/en/display/format',
    type: 'function',
    example: <>
      <p>{ '{{ modifiedTime(\'YYYY-MM-DD\') }}' }</p>
      <span>{ '{{modifiedTime \'\'}}' }</span>
    </>
  },
  {
    key: 'createdTime',
    name: <CopyableText
      textToCopy="createdTime"
      style={{ fontWeight: 'bold', color: 'black', margin: 0 }}
    >
      createdTime
    </CopyableText>,
    // eslint-disable-next-line max-len
    description: 'Функція для отримання дати останнього редагування сторінки в форматі ISO 8601 (якщо формат не вказаний). Більше деталей: https://day.js.org/docs/en/display/format',
    type: 'function',
    example: <>
      <p>{ '{{ createdTime(\'YYYY-MM-DD\') }}' }</p>
      <span>{ '{{createdTime \'\'}}' }</span>
    </>
  },
  {
    key: 'publicationTime',
    name: <CopyableText
      textToCopy="publicationTime"
      style={{ fontWeight: 'bold', color: 'black', margin: 0 }}
    >
      publicationTime
    </CopyableText>,
    // eslint-disable-next-line max-len
    description: 'Функція для отримання дати публікації сторінки в форматі ISO 8601 (якщо формат не вказаний). Повертає порожній рядок, якщо сторінку ще не опубліковано. Більше деталей: https://day.js.org/docs/en/display/format',
    type: 'function',
    example: <>
      <p>{ '{{ publicationTime(\'YYYY-MM-DD\') }}' }</p>
      <span>{ '{{publicationTime \'\'}}' }</span>
    </>
  },
  {
    key: 'getSiteMainDomain',
    name: <CopyableText
      textToCopy="getSiteMainDomain"
      style={{ fontWeight: 'bold', color: 'black', margin: 0 }}
    >
      getSiteMainDomain
    </CopyableText>,
    // eslint-disable-next-line max-len
    description: <>
      Функція для отримання головного домену сайту в форматі FQDN. Параметри:<br/><code>siteId</code> - ID сайту<br/><code>type</code> - тип домену (можливі значення: <code>fqdn</code>, <code>name</code>).
    </>,
    type: 'function',
    example: <>
      <p>{ '{{#getSiteMainDomain \'UUID\' \'fqdn\'}}{{/getSiteMainDomain}}' }</p>
      <span>{ '{{ getSiteMainDomain \'UUID\' \'fqdn\' }}' }</span>
    </>
  },
  {
    key: 'dateNow',
    name: <CopyableText
      textToCopy="dateNow"
      style={{ fontWeight: 'bold', color: 'black', margin: 0 }}
    >
      dateNow
    </CopyableText>,
    description: 'Функція для отримання поточної дати в форматі MM.DD.YYYY',
    type: 'function',
    example: '{{ dateNow }}'
  },
  {
    key: 'dateYear',
    name: <CopyableText
      textToCopy="dateYear"
      style={{ fontWeight: 'bold', color: 'black', margin: 0 }}
    >
      dateYear
    </CopyableText>,
    description: 'Функція для отримання року поточної дати.',
    type: 'function',
    example: '{{ dateYear }}'
  },
];


const columns: TableProps['columns'] = [
  {
    title: 'name',
    dataIndex: 'name',
    key: 'name',
    width: 200,
  },
  {
    title: 'description',
    dataIndex: 'description',
    key: 'description',
  },
  {
    hidden: true,
    title: 'type',
    dataIndex: 'type',
    key: 'type',
  },
  {
    hidden: true,
    title: 'example',
    dataIndex: 'example',
    key: 'example',
  }
];
